"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var react_1 = require("react");
var useCarolineSession_1 = __importDefault(require("../auth/useCarolineSession"));
var client_1 = require("./client");
var store_1 = require("./store");
var store = (0, store_1.createCarolineLibraryStore)(client_1.carolineLibraryClient);
var EMPTY_ITEMS = Object.freeze([]);
var emptySnapshot = function (status, userId) {
    if (userId === void 0) { userId = null; }
    return ({
        status: status,
        items: EMPTY_ITEMS,
        userId: userId,
        error: null,
    });
};
var useCarolineLibrary = function () {
    var _a;
    var authSession = (0, useCarolineSession_1.default)();
    // Until Caroline Auth confirms a user, the app works in local guest mode.
    // An unavailable auth service must not prevent Library use.
    var session = authSession.status === 'authenticated'
        ? authSession
        : { user: null, isAuthenticated: false, status: 'unauthenticated' };
    var sessionRef = (0, react_1.useRef)(session);
    sessionRef.current = session;
    var snapshot = (0, react_1.useSyncExternalStore)(store.subscribe, store.getSnapshot, store.getSnapshot);
    var sessionStatus = session.status;
    var isAuthenticated = session.isAuthenticated;
    var userId = sessionStatus === 'authenticated' && isAuthenticated ? ((_a = session.user) === null || _a === void 0 ? void 0 : _a.id) || null : null;
    (0, react_1.useLayoutEffect)(function () {
        store.setSession({
            status: sessionStatus,
            isAuthenticated: isAuthenticated,
            user: userId === null ? null : { id: userId },
        }).catch(function () { return undefined; });
    }, [sessionStatus, isAuthenticated, userId]);
    var visibleSnapshot;
    if (session.status === 'loading') {
        visibleSnapshot = emptySnapshot('loading');
    }
    else if (session.status === 'unauthenticated' || session.status === 'unavailable') {
        visibleSnapshot = snapshot.userId === null ? snapshot : emptySnapshot('loading');
    }
    else if (userId === null || snapshot.userId !== userId) {
        // Do not expose a previous user's cached references during the render
        // before the session effect starts loading the newly authenticated user.
        visibleSnapshot = emptySnapshot(userId === null ? 'unavailable' : 'loading', userId);
    }
    else {
        visibleSnapshot = snapshot;
    }
    var isInLibrary = (0, react_1.useCallback)(function (mediaType, metaId) {
        var _a;
        var currentSession = sessionRef.current;
        if (visibleSnapshot.status !== 'ready'
            || (userId !== null && (currentSession.status !== 'authenticated'
                || currentSession.isAuthenticated !== true
                || ((_a = currentSession.user) === null || _a === void 0 ? void 0 : _a.id) !== userId)))
            return undefined;
        return store.isInLibrary(mediaType, metaId, userId);
    }, [visibleSnapshot, userId]);
    var add = (0, react_1.useCallback)(function (reference) {
        var _a;
        var currentSession = sessionRef.current;
        var guestSession = userId === null && ['unauthenticated', 'unavailable'].includes(currentSession.status);
        if (!guestSession && (userId === null
            || currentSession.status !== 'authenticated'
            || currentSession.isAuthenticated !== true
            || ((_a = currentSession.user) === null || _a === void 0 ? void 0 : _a.id) !== userId)) {
            return Promise.reject(new store_1.CarolineLibrarySessionError('A Caroline session is required for Library changes.'));
        }
        return store.add(reference, userId);
    }, [userId]);
    var remove = (0, react_1.useCallback)(function (reference) {
        var _a;
        var currentSession = sessionRef.current;
        var guestSession = userId === null && ['unauthenticated', 'unavailable'].includes(currentSession.status);
        if (!guestSession && (userId === null
            || currentSession.status !== 'authenticated'
            || currentSession.isAuthenticated !== true
            || ((_a = currentSession.user) === null || _a === void 0 ? void 0 : _a.id) !== userId)) {
            return Promise.reject(new store_1.CarolineLibrarySessionError('A Caroline session is required for Library changes.'));
        }
        return store.remove(reference, userId);
    }, [userId]);
    var refresh = (0, react_1.useCallback)(function () {
        var _a;
        var currentSession = sessionRef.current;
        var guestSession = userId === null && ['unauthenticated', 'unavailable'].includes(currentSession.status);
        if (!guestSession && (userId === null
            || currentSession.status !== 'authenticated'
            || currentSession.isAuthenticated !== true
            || ((_a = currentSession.user) === null || _a === void 0 ? void 0 : _a.id) !== userId))
            return Promise.resolve(EMPTY_ITEMS);
        return store.refresh(userId);
    }, [userId]);
    return {
        status: visibleSnapshot.status,
        items: visibleSnapshot.items,
        error: visibleSnapshot.error,
        isInLibrary: isInLibrary,
        add: add,
        remove: remove,
        refresh: refresh,
    };
};
exports.default = useCarolineLibrary;
